/**
 * Client-side mesh format conversion.
 *
 * The viewer exports GLB and only GLB. Everything else is produced here by
 * re-parsing that GLB into a three scene and re-serialising it, which is why
 * these formats are a host concern rather than a contract one — no new RPC, no
 * version bump.
 *
 * Every import is lazy: three's exporters are large, and the overwhelmingly
 * common export is the GLB that needs none of them.
 */

/** Re-parse an exported GLB (already glTF-binary) into a three scene. */
async function glbToScene(glb: Blob) {
  const { GLTFLoader } = await import("three/examples/jsm/loaders/GLTFLoader.js");
  const gltf = await new GLTFLoader().parseAsync(await glb.arrayBuffer(), "");
  return gltf.scene;
}

export type MeshFormat = "glb" | "gltf" | "obj" | "usdz";

export const MESH_FORMAT_LABEL: Record<MeshFormat, string> = {
  glb: "GLB",
  gltf: "glTF (.gltf)",
  obj: "OBJ",
  usdz: "USDZ",
};

/** Convert an exported GLB to `format`. Returns the blob and its filename. */
export async function convertGlb(
  glb: Blob,
  format: MeshFormat,
  basename = "model",
): Promise<{ blob: Blob; filename: string }> {
  if (format === "glb") return { blob: glb, filename: `${basename}.glb` };

  const scene = await glbToScene(glb);

  if (format === "obj") {
    const { OBJExporter } = await import("three/examples/jsm/exporters/OBJExporter.js");
    const obj = new OBJExporter().parse(scene);
    return { blob: new Blob([obj], { type: "text/plain" }), filename: `${basename}.obj` };
  }
  if (format === "gltf") {
    const { GLTFExporter } = await import("three/examples/jsm/exporters/GLTFExporter.js");
    const json = await new GLTFExporter().parseAsync(scene, { binary: false });
    return {
      blob: new Blob([JSON.stringify(json)], { type: "model/gltf+json" }),
      filename: `${basename}.gltf`,
    };
  }
  const { USDZExporter } = await import("three/examples/jsm/exporters/USDZExporter.js");
  const usdz = await new USDZExporter().parseAsync(scene);
  return {
    blob: new Blob([usdz as BlobPart], { type: "model/vnd.usdz+zip" }),
    filename: `${basename}.usdz`,
  };
}
